import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  CircularProgress,
  Alert,
  Tooltip,
  Divider,
  Stack,
  Badge,
  Avatar
} from '@mui/material';
import {
  Search,
  FilterList,
  Close,
  History,
  Star,
  StarBorder,
  Archive,
  Unarchive,
  Delete,
  Refresh,
  Visibility,
  Person,
  Business,
  Work,
  Schedule,
  TrendingUp,
  Warning,
  CheckCircle
} from '@mui/icons-material';
import scanHistoryService from '../services/scanHistoryService';
import SkillHighlighting from './SkillHighlighting';

const ScanHistoryModal = ({ open, onClose, onSelectScan }) => {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    minScore: '',
    maxScore: '',
    jobTitle: '',
    company: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  
  const [showSkillHighlighting, setShowSkillHighlighting] = useState(false);
  const [selectedScanForSkills, setSelectedScanForSkills] = useState(null);

  // Load scans when modal opens
  useEffect(() => {
    if (open) {
      loadScans();
    }
  }, [open, pagination.page, searchTerm, filters, sortBy, sortOrder]);

  const handleDeleteScan = async (scanId) => {
    if (!window.confirm('Are you sure you want to delete this scan? This action cannot be undone.')) {
      return;
    }

    try {
      await scanHistoryService.deleteScan(scanId);
      // Reload scans after deletion
      loadScans();
    } catch (error) {
      setError('Failed to delete scan. Please try again.');
    }
  };

  

  const handleViewSkillHighlighting = (scan) => {
    // Normalize saved scan into the shape expected by SkillHighlighting
    const normalized = {
      ...(scan.analysisResults || {}),
      // Provide resume/job text for highlighting when available
      resumeText: scan.resumeText || scan.inputData?.resumeText || '',
      jobDescription: scan.jobDescription || scan.inputData?.jobDescription || '',
      // Preserve raw step timings if present (for fast-mode UI hints)
      raw: scan.processingMetadata || {},
    };
    setSelectedScanForSkills(normalized);
    setShowSkillHighlighting(true);
  };

  const loadScans = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sortBy,
        sortOrder,
        search: searchTerm || undefined,
        ...filters
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await scanHistoryService.getScanHistory(params);
      setScans(response.data || []);
      setPagination(prev => ({
        ...prev,
        total: response.pagination?.total || 0,
        pages: response.pagination?.pages || 0
      }));
    } catch (err) {
      setError('Failed to load scan history');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handlePageChange = (event, page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handleSelectScan = (scan) => {
    onSelectScan(scan);
    onClose();
  };

  const handleRefresh = () => {
    loadScans();
  };

  const clearFilters = () => {
    setFilters({
      minScore: '',
      maxScore: '',
      jobTitle: '',
      company: ''
    });
    setSearchTerm('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const formatScan = (scan) => {
    return scanHistoryService.formatScanForDisplay(scan);
  };

  const getScoreColor = (score) => scanHistoryService.getScoreColor(score);
  const getVerdictColor = (verdict) => scanHistoryService.getVerdictColor(verdict);
  const formatDate = (dateString) => scanHistoryService.formatDate(dateString);
  const formatRelativeTime = (dateString) => scanHistoryService.formatRelativeTime(dateString);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh', maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <History color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Scan History
          </Typography>
          <Badge badgeContent={pagination.total} color="primary" />
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Search and Filters */}
        <Box sx={{ p: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search scans..."
              value={searchTerm}
              onChange={handleSearch}
              size="small"
              sx={{ minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Min Score</InputLabel>
              <Select
                value={filters.minScore}
                onChange={(e) => handleFilterChange('minScore', e.target.value)}
                label="Min Score"
              >
                <MenuItem value="">Any</MenuItem>
                <MenuItem value="80">80+</MenuItem>
                <MenuItem value="60">60+</MenuItem>
                <MenuItem value="40">40+</MenuItem>
                <MenuItem value="0">0+</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                label="Sort By"
              >
                <MenuItem value="createdAt">Date</MenuItem>
                <MenuItem value="analysisResults.scores.overall.score">Score</MenuItem>
                <MenuItem value="inputData.jobTitle">Job Title</MenuItem>
                <MenuItem value="inputData.company">Company</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={clearFilters}
              size="small"
            >
              Clear Filters
            </Button>

            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              size="small"
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Scan List */}
        <Box sx={{ p: 3, pt: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : scans.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <History sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No scans found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm || Object.values(filters).some(f => f) 
                  ? 'Try adjusting your search or filters'
                  : 'Your scan history will appear here after you run analyses'
                }
              </Typography>
            </Box>
          ) : (
            <>
              {scans.map((scan, index) => {
                const formattedScan = formatScan(scan);
                return (
                  <Card 
                    key={scan.scanId || index} 
                    sx={{ 
                      mb: 2, 
                      cursor: 'pointer',
                      '&:hover': { 
                        boxShadow: 3,
                        transform: 'translateY(-1px)',
                        transition: 'all 0.2s ease-in-out'
                      }
                    }}
                    onClick={() => handleSelectScan(scan)}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" fontWeight={600} gutterBottom>
                            {formattedScan.jobTitle}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            {formattedScan.company && (
                              <>
                                <Business fontSize="small" color="action" />
                                <Typography variant="body2" color="text.secondary">
                                  {formattedScan.company}
                                </Typography>
                                <Divider orientation="vertical" flexItem />
                              </>
                            )}
                            <Person fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {formattedScan.candidateName}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={`${formattedScan.overallScore}/100`}
                            size="small"
                            sx={{
                              bgcolor: getScoreColor(formattedScan.overallScore),
                              color: 'white',
                              fontWeight: 600
                            }}
                          />
                          <Chip
                            label={formattedScan.verdict}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: getVerdictColor(formattedScan.verdict),
                              color: getVerdictColor(formattedScan.verdict)
                            }}
                          />
                          
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewSkillHighlighting(scan);
                            }}
                            sx={{ ml: 1 }}
                            title="View Skill Matching"
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteScan(scan.scanId);
                            }}
                            sx={{ ml: 1 }}
                            title="Delete Scan"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Schedule fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {formatRelativeTime(formattedScan.createdAt)}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TrendingUp fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {Math.round(formattedScan.processingTime / 1000)}s
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CheckCircle fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {formattedScan.preview.coveragePercent}% required skills
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        <Chip
                          label={`Experience: ${formattedScan.preview.experienceScore}/28`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`Technical Skills: ${formattedScan.preview.skillsScore}/26`}
                          size="small"
                          variant="outlined"
                        />
                        {formattedScan.preview.riskFlags > 0 && (
                          <Chip
                            icon={<Warning />}
                            label={`${formattedScan.preview.riskFlags} risk flags`}
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                        <Chip
                          label={`${formattedScan.preview.coveredSkills}/${formattedScan.preview.mustHaveSkills} required skills (${formattedScan.preview.coveragePercent}%)`}
                          size="small"
                          variant="outlined"
                        />
                      </Box>

                      {/* Skills Preview Section */}
                      {(() => {
                        // Extract skill data from scan
                        const matchedSkills = scan.analysisResults?.keywordMatches?.mustHaveCovered || 
                                            scan.analysisResults?.keywordMatches?.criticalMatchedSkills || [];
                        const missingSkills = scan.analysisResults?.keywordMatches?.mustHaveMissing || 
                                            scan.analysisResults?.keywordMatches?.criticalMissingSkills || [];
                        const bonusSkills = scan.analysisResults?.keywordMatches?.niceToHaveMatched || 
                                          scan.analysisResults?.keywordMatches?.niceToHaveSkills || [];
                        
                        const hasAnySkills = matchedSkills.length > 0 || missingSkills.length > 0 || bonusSkills.length > 0;
                        
                        if (!hasAnySkills) return null;

                        return (
                          <Box sx={{ mt: 1.5 }}>
                            {matchedSkills.length > 0 && (
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="caption" fontWeight={600} color="success.main" sx={{ display: 'block', mb: 0.5 }}>
                                  ✅ Skills Matched (Required):
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  {matchedSkills.slice(0, 10).map((skill, idx) => (
                                    <Chip
                                      key={idx}
                                      label={skill}
                                      size="small"
                                      sx={{ 
                                        bgcolor: 'success.50',
                                        color: 'success.main',
                                        fontSize: '0.7rem',
                                        height: 20
                                      }}
                                    />
                                  ))}
                                  {matchedSkills.length > 10 && (
                                    <Chip
                                      label={`+${matchedSkills.length - 10} more`}
                                      size="small"
                                      sx={{ 
                                        bgcolor: 'success.50',
                                        color: 'success.main',
                                        fontSize: '0.7rem',
                                        height: 20
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            )}

                            {missingSkills.length > 0 && (
                              <Box sx={{ mb: 1 }}>
                                <Typography variant="caption" fontWeight={600} color="error.main" sx={{ display: 'block', mb: 0.5 }}>
                                  ❌ Skills Missing (Required):
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  {missingSkills.slice(0, 10).map((skill, idx) => (
                                    <Chip
                                      key={idx}
                                      label={skill}
                                      size="small"
                                      sx={{ 
                                        bgcolor: 'error.50',
                                        color: 'error.main',
                                        fontSize: '0.7rem',
                                        height: 20
                                      }}
                                    />
                                  ))}
                                  {missingSkills.length > 10 && (
                                    <Chip
                                      label={`+${missingSkills.length - 10} more`}
                                      size="small"
                                      sx={{ 
                                        bgcolor: 'error.50',
                                        color: 'error.main',
                                        fontSize: '0.7rem',
                                        height: 20
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            )}

                            {bonusSkills.length > 0 && (
                              <Box>
                                <Typography variant="caption" fontWeight={600} color="warning.main" sx={{ display: 'block', mb: 0.5 }}>
                                  ⭐ Bonus Skills Matched (Preferred):
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                  {bonusSkills.slice(0, 10).map((skill, idx) => (
                                    <Chip
                                      key={idx}
                                      label={skill}
                                      size="small"
                                      sx={{ 
                                        bgcolor: 'warning.50',
                                        color: 'warning.main',
                                        fontSize: '0.7rem',
                                        height: 20
                                      }}
                                    />
                                  ))}
                                  {bonusSkills.length > 10 && (
                                    <Chip
                                      label={`+${bonusSkills.length - 10} more`}
                                      size="small"
                                      sx={{ 
                                        bgcolor: 'warning.50',
                                        color: 'warning.main',
                                        fontSize: '0.7rem',
                                        height: 20
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        );
                      })()}
                    </CardContent>
                  </Card>
                );
              })}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {scans.length} of {pagination.total} scans
          </Typography>
          
          {pagination.pages > 1 && (
            <Pagination
              count={pagination.pages}
              page={pagination.page}
              onChange={handlePageChange}
              color="primary"
              size="small"
            />
          )}
        </Box>
      </DialogActions>

      

      {/* Skill Highlighting Modal */}
      <Dialog
        open={showSkillHighlighting}
        onClose={() => setShowSkillHighlighting(false)}
        maxWidth="xl"
        fullWidth
        aria-labelledby="skill-highlighting-modal"
      >
        <DialogContent sx={{ p: 0 }}>
          <SkillHighlighting
            scanData={selectedScanForSkills}
            onClose={() => setShowSkillHighlighting(false)}
          />
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default ScanHistoryModal;
